import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import t from 'react-translate';

import { getCourseAliases, getCurrentCourse } from 'redux/selectors/course';
import { AnalyticsPageType, ConfigurationType, QuestionMapping, ReportConfiguration } from 'redux/schemas/app/analytics';
import { AnalyticsReportConfigurationRequest } from 'redux/schemas/api/analytics';
import { useAppDispatch } from 'redux/store';
import {
  addConfiguration,
  distributionCharts,
  fetchConfiguration,
  hasExistingConfiguration,
  surveyStats,
  updateConfiguration,
} from 'redux/actions/analytics';

import { AnalyticsContext } from '../analytics-dashboard';
import SelectSurvey from './select-survey';
import QuestionsMapping from './questions-mapping';
import { PrePostSurveySetupModalProps } from '.';

interface PrePostSurveyFormData {
  postCourseCatalogId: string;
  preCourseSurveyId: number;
  postCourseSurveyId: number;
  questionMappings: QuestionMapping[];
  npsSurveyId?: number | null;
  npsQuestionId?: number | null;
  csatSurveyId?: number | null;
  csatQuestionId?: number | null;
}

const PrePostSurveySetupModalContent: React.FC<PrePostSurveySetupModalProps> = ({ onClose }) => {
  const {
    analyticsPageType, catalogId, institutionId,
    prePostSurveyConfigurationId: configurationId,
  } = useContext(AnalyticsContext);

  const currentCourse = useSelector(getCurrentCourse);
  const aliases = useSelector(getCourseAliases);

  const initialCourse = analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS && currentCourse?.isCohort
    ? { ...currentCourse.primary, isPrimary: true } : currentCourse;

  const schema = yup.object().shape({
    // postCourseCatalogId, preCourseSurveyId, and postCourseSurveyId are validated
    // as they are required to proceed to question mapping step
    questionMappings: yup.array()
      .of(
        yup.object().shape({
          preQuestionId: yup.number().required(),
          postQuestionId: yup.number().required(),
        }),
      )
      .min(1, t.COURSE_ANALYTICS.PRE_POST.MODAL.VALIDATION())
      .required(),
  });

  const methods = useForm<PrePostSurveyFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      postCourseCatalogId: configurationId ? null : initialCourse.catalogId,
      preCourseSurveyId: null,
      postCourseSurveyId: null,
      questionMappings: [],
    },
  });

  const { handleSubmit, watch } = methods;

  const postCourseCatalogId = watch('postCourseCatalogId');
  const preSurveyId = watch('preCourseSurveyId');
  const postSurveyId = watch('postCourseSurveyId');

  const preCourseCatalogId = initialCourse?.catalogId;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (configurationId) {
      // Load existing configuration and set form values accordingly
      const setConfigurationData = async (data: ReportConfiguration) => {
        const { postCourse, preCourseSurvey, postCourseSurvey, questionMappings, hasAccessToPostCourse, metricConfiguration } = data;
        if (!hasAccessToPostCourse) {
          return;
        }
        const existingConfig: PrePostSurveyFormData = {
          postCourseSurveyId: postCourseSurvey.id,
          preCourseSurveyId: preCourseSurvey.id,
          postCourseCatalogId: postCourse.catalogId,
          questionMappings,
          npsSurveyId: metricConfiguration?.npsSurveyId,
          npsQuestionId: metricConfiguration?.npsQuestionId,
          csatSurveyId: metricConfiguration?.csatSurveyId,
          csatQuestionId: metricConfiguration?.csatQuestionId,
        };
        methods.reset(existingConfig);
      };
      dispatch(fetchConfiguration({
        configurationId,
        preCourseCatalogId: catalogId,
        institutionId,
      })).then(async (result) => {
        const { payload } = result as { payload: ReportConfiguration };
        if (!payload) return;
        await setConfigurationData(payload);
      });
    }
  }, [catalogId, configurationId, dispatch, institutionId, methods]);

  const onSubmit = async (data: PrePostSurveyFormData) => {
    const submitAction = configurationId ? updateConfiguration : addConfiguration;
    const configurationData: AnalyticsReportConfigurationRequest = {
      institutionId: currentCourse.institutionId,
      preCourseCatalogId: catalogId,
      preQuestionSetId: data.preCourseSurveyId,
      postQuestionSetId: data.postCourseSurveyId,
      configurationType: ConfigurationType[analyticsPageType],
      questionMappings: data.questionMappings,
      npsQuestionId: data.npsQuestionId,
      csatQuestionId: data.csatQuestionId,
    };
    if (configurationId) {
      configurationData.configurationId = configurationId;
    }
    try {
      const result = await dispatch(submitAction(configurationData));
      if (!result?.error) {
        await Promise.all([
          dispatch(hasExistingConfiguration({
            institutionId,
            preCourseCatalogId: catalogId,
            configurationType: ConfigurationType[analyticsPageType],
          })),
          ...(configurationId ? [
            dispatch(distributionCharts({ institutionId, preCourseCatalogId: catalogId, configurationId })),
            dispatch(surveyStats({ institutionId, preCourseCatalogId: catalogId, configurationId })),
          ] : []),
        ]);
        onClose?.();
      }
    } catch (error) {
      // Error alert already handled in redux action
      console.error('Failed to add configuration:', error);
    }
  };

  return (
    <div className='d-flex flex-column p-4 text-large-body'>
      <div className='font-weight-bolder'>
        {t.COURSE_ANALYTICS.PRE_POST.MODAL.SELECT()}
      </div>
      <div className='my-4'>
        <div>
          {t.COURSE_ANALYTICS.PRE_POST.MODAL.DESCRIPTION_1({
            ...aliases.learnersAliases, ...aliases.courseAliases,
          })}
        </div>
        {analyticsPageType === AnalyticsPageType.COHORT_ANALYTICS && (
          <div>
            {t.COURSE_ANALYTICS.PRE_POST.MODAL.DESCRIPTION_2()}
          </div>
        )}
      </div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='mb-5'>
            <SelectSurvey
              title={t.COURSE_ANALYTICS.PRE_POST.MODAL.PRE()}
              surveyIdToExclude={postCourseCatalogId === preCourseCatalogId ? postSurveyId : null}
              formName='preCourseSurveyId'
              preCatalogId={preCourseCatalogId}
            />
            <SelectSurvey
              title={t.COURSE_ANALYTICS.PRE_POST.MODAL.POST()}
              canEditCourse
              disabled={!preSurveyId}
              surveyIdToExclude={postCourseCatalogId === preCourseCatalogId ? preSurveyId : null}
              formName='postCourseSurveyId'
            />
          </div>
          {preSurveyId && postSurveyId && (
            <QuestionsMapping
              preCatalogId={preCourseCatalogId}
            />
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default PrePostSurveySetupModalContent;
